/**
 * Budget Live — real-time budget telemetry.
 *
 * Role-aware by design:
 *  - Managers (owner/lead): full control — deposit funds, adjust budget.
 *  - Developers: read-only telemetry. Budget control is never exposed.
 * Self-contained styling (dashboard-pages.css).
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  Activity,
  ArrowRight,
  Flame,
  Lock,
  Plus,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { apiPatch } from '../api';
import { useTeamDashboard } from '../TeamDashboardContext';
import './dashboard-pages.css';

const T = {
  accent: '#6e6bff',
  accent2: '#a855f7',
  cyan: '#22d3ee',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb4d6d',
};

const FONT_ID = 'tdp-fonts';
function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const Card: React.FC<{ children: React.ReactNode; className?: string; delay?: number; style?: React.CSSProperties }> = ({ children, className = '', delay = 0, style }) => (
  <motion.section
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={`tdp-card ${className}`}
    style={style}
  >
    {children}
  </motion.section>
);

const SectionTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="tdp-section-title">
    <span>{children}</span>
    {right}
  </div>
);

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const MEMBER_COLORS = [T.accent, T.amber, T.green, T.red];

const DEMO_DEDUCTIONS = [
  { initials: 'AK', name: 'Arjun Kumar', task: 'API routes v2', hrs: '2.5', rate: 500, deducted: 1250 },
  { initials: 'SR', name: 'Sneha Rao', task: 'DB schema review', hrs: '1.8', rate: 400, deducted: 720 },
  { initials: 'PK', name: 'Priya Kapoor', task: 'Auth middleware', hrs: '1.3', rate: 500, deducted: 650 },
];

export default function WcBudgetPage() {
  const { showToast, isReal, isManager, budget, members, teamSlug, theme } = useTeamDashboard();
  const [addAmt, setAddAmt] = useState('25000');
  const [animated, setAnimated] = useState(false);
  const [budgetOverride, setBudgetOverride] = useState<{ total: number; spent: number; remaining: number } | null>(null);

  const liveBudget = budgetOverride || {
    total: Number(budget?.total || 0),
    spent: Number(budget?.spent || 0),
    remaining: Number(budget?.remaining || 0),
  };
  const spentPct = liveBudget.total > 0 ? Math.max(0, Math.min(100, Math.round((liveBudget.spent / liveBudget.total) * 100))) : 0;
  const remainingPct = 100 - spentPct;
  const workingMembers = members.filter((m) => m.status === 'working');
  const burnRate = workingMembers.reduce((sum, m) => sum + Number(m.rate || 0), 0);

  useEffect(() => {
    ensureFonts();
    const t = setTimeout(() => setAnimated(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setBudgetOverride(null);
  }, [budget?.total, budget?.spent, budget?.remaining]);

  async function handleDepositFunds() {
    if (!isManager) return; // hard guard — developers can never mutate budget
    const amount = Number(addAmt);
    if (!Number.isFinite(amount) || amount <= 0) {
      showToast('Deposit funds', 'Enter a valid amount', 'error');
      return;
    }
    if (!isReal) {
      showToast('Funds deposited', `₹${amount.toLocaleString()} added (preview)`, 'success');
      return;
    }
    const nextTotal = Math.max(0, liveBudget.total + amount);
    try {
      await apiPatch(`/${teamSlug}/budget`, { totalBudget: nextTotal });
      setBudgetOverride({
        total: nextTotal,
        spent: liveBudget.spent,
        remaining: Math.max(0, nextTotal - liveBudget.spent),
      });
      showToast('Funds deposited', `₹${amount.toLocaleString()} added to workspace budget`, 'success');
    } catch {
      showToast('Deposit funds', 'Could not update budget in real mode', 'error');
    }
  }

  const deductions = isReal
    ? members.map((m) => ({
        initials: m.initials,
        name: m.name,
        task: m.status === 'working' ? 'Active project work' : 'No active assignment',
        hrs: Number(m.hoursThisWeek || 0).toFixed(1),
        rate: m.rate,
        deducted: Math.round(Number(m.totalSpent || 0)),
      }))
    : DEMO_DEDUCTIONS;

  const weeklySeries = isReal
    ? members.map((m, i) => ({
        initials: m.initials,
        name: m.name,
        color: MEMBER_COLORS[i % MEMBER_COLORS.length],
        vals: WEEK_DAYS.map(() => Math.round(Number(m.totalSpent || 0) / 5)),
      }))
    : [
        { initials: 'AK', name: 'Arjun K.', color: T.accent, vals: [4000, 3500, 4000, 4500, 3800] },
        { initials: 'SR', name: 'Sneha R.', color: T.amber, vals: [3200, 2800, 3600, 3200, 0] },
        { initials: 'PK', name: 'Priya K.', color: T.green, vals: [4000, 3500, 2000, 0, 4000] },
        { initials: 'RM', name: 'Rahul M.', color: T.red, vals: [0, 2400, 2400, 1500, 1800] },
      ];

  const isDark = theme === 'dark';
  const healthColor = remainingPct > 40 ? T.green : remainingPct > 15 ? T.amber : T.red;

  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="tdp" style={{ minHeight: '100%' }}>
      <div className="tdp-wrap">
        {/* hero */}
        <div className="tdp-hero">
          <div className="tdp-hero-row">
            <div className="tdp-hero-main">
              <div className="tdp-eyebrow">
                <span className="tdp-live-dot" />
                Work clock · budget live
              </div>
              <h1 className="tdp-title">Budget Live</h1>
              <p className="tdp-sub">Real-time budget tracking — every hour worked deducts automatically.</p>
            </div>
            {!isManager && (
              <span className="tdp-pill" style={{ background: 'var(--tdp-hover)', color: 'var(--tdp-dim)', padding: '6px 12px' }}>
                <Lock size={9} /> Read-only · managed by your team lead
              </span>
            )}
            {isManager && (
              <span className="tdp-pill" style={{ background: `${T.green}1a`, color: T.green, padding: '6px 12px' }}>
                <ShieldCheck size={9} /> Manager controls active
              </span>
            )}
          </div>
        </div>

        {/* KPI row */}
        <div className="tdp-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
          {[
            { label: 'Remaining', value: `₹${liveBudget.remaining.toLocaleString()}`, sub: `${remainingPct}% left`, color: healthColor, icon: Wallet, down: false },
            { label: 'Spent', value: `₹${liveBudget.spent.toLocaleString()}`, sub: `${spentPct}% used`, color: T.accent, icon: Activity, down: true },
            { label: 'Burn rate', value: `₹${burnRate.toLocaleString()}`, sub: '/hr live', color: T.amber, icon: Flame, down: false },
          ].map((kpi, i) => (
            <Card key={kpi.label} delay={0.05 + i * 0.05} className="tdp-kpi">
              <span className="tdp-kpi-icon" style={{ background: `${kpi.color}18`, color: kpi.color }}>
                <kpi.icon size={14} />
              </span>
              <div className="tdp-kpi-value" style={{ color: kpi.label === 'Remaining' ? healthColor : undefined }}>
                {kpi.value}
              </div>
              <div className="tdp-kpi-label">{kpi.label}</div>
              <div className="tdp-kpi-sub" style={{ color: kpi.color, display: 'flex', alignItems: 'center', gap: 3 }}>
                {kpi.down ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
                {kpi.sub}
              </div>
            </Card>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 18, marginBottom: 22 }} className="tdp-budget-grid">
          <style>{`@media (min-width: 1080px) { .tdp-budget-grid { grid-template-columns: minmax(0,1fr) minmax(0,1fr) !important; } }`}</style>

          {/* deposit — MANAGERS ONLY */}
          {isManager ? (
            <Card delay={0.14} style={{ padding: 20 }}>
              <div className="tdp-section-title" style={{ margin: '0 0 12px' }}>
                <span>Add funds</span>
                <ShieldCheck size={11} style={{ color: T.green }} />
              </div>
              <label className="tdp-label">Amount (₹)</label>
              <input className="tdp-input" type="number" value={addAmt} onChange={(e) => setAddAmt(e.target.value)} />
              <div style={{ display: 'flex', gap: 6, margin: '10px 0 14px' }}>
                {['10000', '25000', '50000'].map((v) => (
                  <button key={v} className="tdp-btn tdp-btn-ghost tdp-btn-sm" onClick={() => setAddAmt(v)}>
                    ₹{parseInt(v, 10) / 1000}k
                  </button>
                ))}
              </div>
              <button className="tdp-btn tdp-btn-primary" style={{ width: '100%' }} onClick={() => void handleDepositFunds()}>
                <Plus size={13} /> Deposit funds
              </button>
              <div className="tdp-feed-meta" style={{ marginTop: 10, textAlign: 'center' }}>
                Deposits apply instantly and notify the team.
              </div>
            </Card>
          ) : (
            /* read-only explainer for developers */
            <Card delay={0.14} style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: 'var(--tdp-hover)',
                  color: 'var(--tdp-faint)',
                  marginBottom: 12,
                }}
              >
                <Lock size={19} />
              </span>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--tdp-text)' }}>Budget is manager-controlled</div>
              <p style={{ margin: '6px 0 0', fontSize: 11.5, lineHeight: 1.6, color: 'var(--tdp-dim)', maxWidth: 300 }}>
                You have full visibility — balances, burn rate and deduction logs — but only owners and leads can deposit or adjust funds.
              </p>
            </Card>
          )}

          {/* budget health */}
          <Card delay={0.18} style={{ padding: 20 }}>
            <div className="tdp-section-title" style={{ margin: '0 0 12px' }}>
              <span>Budget health</span>
              <span className="tdp-pill" style={{ background: `${healthColor}1a`, color: healthColor }}>
                {remainingPct > 40 ? 'safe' : remainingPct > 15 ? 'watch' : 'critical'}
              </span>
            </div>
            <div className="tdp-mono" style={{ fontSize: 28, fontWeight: 700, color: healthColor }}>
              ₹{liveBudget.remaining.toLocaleString()}
            </div>
            <div className="tdp-feed-meta" style={{ margin: '4px 0 12px' }}>
              of ₹{liveBudget.total.toLocaleString()} · {remainingPct}% remaining
            </div>
            <div className="tdp-progress-track" style={{ height: 7 }}>
              <motion.div
                className="tdp-progress-fill"
                style={{ background: `linear-gradient(90deg, ${healthColor}, ${T.cyan})` }}
                initial={{ width: 0 }}
                animate={{ width: animated ? `${Math.max(remainingPct, 2)}%` : 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7, fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: 'var(--tdp-faint)' }}>
              <span>₹0</span>
              <span style={{ color: healthColor }}>{remainingPct}% left</span>
              <span>₹{liveBudget.total.toLocaleString()}</span>
            </div>
            {burnRate > 0 && liveBudget.remaining > 0 && (
              <div className="tdp-feed-meta" style={{ marginTop: 12 }}>
                At the current burn of ₹{burnRate.toLocaleString()}/hr, funds last ≈{' '}
                <b style={{ color: 'var(--tdp-text)' }}>{Math.floor(liveBudget.remaining / Math.max(burnRate, 1))} working hours</b>.
              </div>
            )}
          </Card>
        </div>

        {/* deduction log */}
        <div className="tdp-section">
          <SectionTitle right={<span className="tdp-mono" style={{ fontSize: 9, color: 'var(--tdp-faint)' }}>auto-deducted per hour</span>}>
            Hourly deduction log
          </SectionTitle>
          <Card delay={0.22}>
            <div className="tdp-table-head">
              <span>Member</span>
              <span>Task</span>
              <span style={{ textAlign: 'right' }}>Hours</span>
              <span style={{ textAlign: 'right' }}>Rate</span>
              <span style={{ textAlign: 'right' }}>Deducted</span>
            </div>
            {deductions.map((row, i) => (
              <motion.div key={`${row.initials}-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.26 + i * 0.05 }} className="tdp-table-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                  <span className="tdp-avatar" style={{ width: 26, height: 26, fontSize: 8.5, background: `linear-gradient(135deg, ${MEMBER_COLORS[i % 4]}, ${T.accent2})` }}>
                    {row.initials}
                  </span>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--tdp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.name}
                  </span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--tdp-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.task}</span>
                <span className="tdp-table-num">{row.hrs}</span>
                <span className="tdp-table-num">₹{row.rate}/hr</span>
                <span className="tdp-table-num" style={{ color: T.red, fontWeight: 700 }}>
                  −₹{row.deducted.toLocaleString()}
                </span>
              </motion.div>
            ))}
            {deductions.length === 0 && (
              <div className="tdp-feed-meta" style={{ padding: 22, textAlign: 'center' }}>
                No deductions logged yet today.
              </div>
            )}
          </Card>
        </div>

        {/* weekly earnings */}
        <div className="tdp-section">
          <SectionTitle right={<span className="tdp-mono" style={{ fontSize: 9, color: 'var(--tdp-faint)' }}>Mon – Fri</span>}>
            Weekly earnings by member
          </SectionTitle>
          <Card delay={0.26} style={{ padding: '18px 20px 14px' }}>
            <div className="tdp-bars">
              {WEEK_DAYS.map((day, dayIdx) => (
                <div key={day} className="tdp-bar-col">
                  <div className="tdp-bar-stack">
                    {weeklySeries.map((m) => {
                      const maxVal = Math.max(...weeklySeries.flatMap((s) => s.vals), 1);
                      const h = animated ? Math.round((m.vals[dayIdx] / maxVal) * 100) : 0;
                      return m.vals[dayIdx] > 0 ? (
                        <div key={m.initials} title={`${m.name}: ₹${m.vals[dayIdx].toLocaleString()}`} className="tdp-bar" style={{ height: `${h}%`, minHeight: 3, background: m.color }} />
                      ) : null;
                    })}
                  </div>
                  <span className="tdp-bar-day">{day}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 14, flexWrap: 'wrap' }}>
              {weeklySeries.map((m) => (
                <span key={m.initials} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'JetBrains Mono, monospace', fontSize: 9, color: 'var(--tdp-dim)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 3, background: m.color, display: 'inline-block' }} />
                  {m.name}
                </span>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
